import { useEffect, useRef, useState, Children, cloneElement, isValidElement } from "react";

/**
 * Scroll-triggered reveal wrapper.
 * Adds .is-visible to its child when it enters the viewport (uses IntersectionObserver).
 * Variants: fade-up | fade-in | scale-in | blur-in | slide-left | slide-right
 */
export default function Reveal({
  children,
  variant = "fade-up",
  delay = 0,
  duration,
  threshold = 0.15,
  once = true,
  as: Tag = "div",
  className = "",
  style: styleProp = {},
  ...rest
}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            if (once) obs.unobserve(entry.target);
          } else if (!once) {
            setVisible(false);
          }
        });
      },
      { threshold, rootMargin: "0px 0px -80px 0px" }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [threshold, once]);

  const style = {
    transitionDelay: `${delay}ms`,
    ...(duration ? { transitionDuration: `${duration}ms` } : {}),
    ...styleProp,
  };

  return (
    <Tag
      ref={ref}
      data-variant={variant}
      className={`reveal ${visible ? "is-visible" : ""} ${className}`}
      style={style}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/**
 * Children-staggering helper. Wraps each direct child in a <Reveal> with an incrementing delay.
 * Use when you want a group of cards to animate one-by-one.
 */
export function Stagger({ children, step = 90, base = 0, variant = "fade-up", as = "div", className = "", ...rest }) {
  const items = Children.toArray(children);
  return (
    <div className={className} {...rest}>
      {items.map((child, i) => {
        if (!isValidElement(child)) return child;
        return (
          <Reveal key={child.key || i} variant={variant} delay={base + i * step} as={as}>
            {child}
          </Reveal>
        );
      })}
    </div>
  );
}
